import { flip, offset, shift, useFloating } from "@floating-ui/react-dom";
import { type Editor } from "@tiptap/core";
import { type NodeViewProps, NodeViewWrapper, useEditorState } from "@tiptap/react";
import { Progress } from "antd";
import { Resizable } from "re-resizable";
import { useEffect, useMemo, useRef, useState } from "react";
import { ErrorColor, PrimaryColor } from "styles/buildertrendTheme/Colors";

import { cn } from "@btnet/utilities-core";

import { BdsButton, BdsIconArrowClockwise } from "@buildertrend/components";

import { reportError } from "utilities/errorHelpers";
import { getBlobFromUrl, getLocalBlobUrlFromExternalResource } from "utilities/file/file";
import { parseIntWithDefault } from "utilities/number/number";

import { BTIconImageOutlined, BTIconLoadingOutlined } from "commonComponents/btWrappers/BTIcon";
import { BTLightbox } from "commonComponents/btWrappers/BTLightbox/BTLightbox";
import { BTRow } from "commonComponents/btWrappers/BTRow/BTRow";
import { ImageOptionsOverlay } from "commonComponents/btWrappers/editor/core/BTEditorContent/overlays/ImageOptionsOverlay/ImageOptionsOverlay";
import { fastIsNodeInSelection } from "commonComponents/btWrappers/editor/core/editor.utils";
import {
    type IFetchErrorState,
    type ImageState,
    type IUploadErrorState,
} from "commonComponents/btWrappers/editor/core/extensions/custom/image/image.types";
import {
    type IImageAttributes,
    type IImageOptions,
    type IImageStorage,
} from "commonComponents/btWrappers/editor/core/extensions/custom/image/ImageExtension";

function isErrorState(state: ImageState): state is IFetchErrorState | IUploadErrorState {
    return state.stateType === "fetchError" || state.stateType === "uploadError";
}

function getInitialImageState(attributes: IImageAttributes, options: IImageOptions): ImageState {
    if (!options.uploadConfig) {
        return {
            stateType: "loaded",
            imageLocalUrl: attributes.src,
        };
    }

    if (attributes.src.startsWith("blob:")) {
        if (attributes.uploadMetadata) {
            return {
                stateType: "loaded",
                imageLocalUrl: attributes.src,
            };
        }

        return {
            stateType: "preupload",
            imageLocalUrl: attributes.src,
            uploadProgressPercent: 0,
        };
    }

    return {
        stateType: "prefetch",
        imageLocalUrl: null,
    };
}

interface IImageNodeViewProps extends NodeViewProps {
    /** Per-NodeView registry key, injected by `ImageExtension.addNodeView`. Stable across attr updates. */
    viewKey: symbol;
}

export const ImageNodeView = ({
    selected: isSolelySelected,
    editor,
    node,
    extension,
    updateAttributes,
    viewKey,
}: IImageNodeViewProps) => {
    const attributes = node.attrs as IImageAttributes;
    const options = extension.options as IImageOptions;
    const [isWithinTextSelection, setIsWithinTextSelection] = useState<boolean>(false);
    const [isLightboxOpen, setIsLightboxOpen] = useState(false);
    const floatingManager = useFloating({ middleware: [flip(), shift({ padding: 5 }), offset(5)] });
    const [imageState, setImageState] = useState<ImageState>(
        getInitialImageState(attributes, options),
    );
    const preferredWidth = parseIntWithDefault(attributes.preferredWidth, undefined);
    const preferredHeight = parseIntWithDefault(attributes.preferredHeight, undefined);
    // User-resized dimensions take precedence; fall back to natural dimensions for unresized images.
    const expectedWidth = attributes.width ?? preferredWidth;
    const expectedHeight = attributes.height ?? preferredHeight;
    const requestAbortController = useRef(new AbortController());

    const storage = editor.storage[extension.name] as IImageStorage;
    const onImageStateChange = options.onImageStateChange;

    const { isEditorFocused, isEditorEditable } = useEditorState({
        editor,
        selector: ({ editor: state }) => ({
            isEditorFocused: state.isFocused,
            isEditorEditable: state.isEditable,
        }),
    });

    useEffect(() => {
        setImageState(getInitialImageState(attributes, options));
        // options is a new object each access; depend on primitives to avoid infinite re-renders.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [attributes.src, attributes.uploadMetadata, options.uploadConfig]);

    useEffect(() => {
        requestAbortController.current = new AbortController();
        return () => {
            requestAbortController.current.abort();
        };
    }, []);

    // Mirror state transitions into the registry. Registration and cleanup are owned by the
    // NodeView wrapper in ImageExtension.
    useEffect(() => {
        storage.imageStates.set(viewKey, imageState.stateType);
        onImageStateChange?.(storage.imageStates);
    }, [imageState.stateType, storage, onImageStateChange, viewKey]);

    useEffect(() => {
        const onSelectionUpdate = ({
            editor: selectionUpdateEditor,
        }: {
            editor: Editor;
            transaction: Editor["state"]["tr"];
        }) => {
            setIsWithinTextSelection(
                fastIsNodeInSelection(
                    selectionUpdateEditor.state.doc,
                    selectionUpdateEditor.state.selection,
                )(node),
            );
        };

        editor.on("selectionUpdate", onSelectionUpdate);

        return () => {
            editor.off("selectionUpdate", onSelectionUpdate);
        };
    }, [editor, node]);

    // Trigger fetch
    useEffect(() => {
        if (imageState.stateType === "prefetch") {
            setImageState({ stateType: "fetching", imageLocalUrl: null, fetchProgressPercent: 0 });
            (async () => {
                try {
                    let blobUrl: string;
                    if (options.uploadConfig && attributes.uploadMetadata) {
                        const downloadData = await options.uploadConfig.onDownloadStaticRef(
                            attributes.src,
                            attributes.uploadMetadata,
                            (progress) => {
                                setImageState({
                                    stateType: "fetching",
                                    imageLocalUrl: null,
                                    fetchProgressPercent: progress,
                                });
                            },
                            requestAbortController.current.signal,
                        );
                        blobUrl = downloadData.blobUrl;
                    } else {
                        blobUrl = await getLocalBlobUrlFromExternalResource(attributes.src, {
                            onProgressUpdate: (progress) => {
                                setImageState({
                                    stateType: "fetching",
                                    imageLocalUrl: null,
                                    fetchProgressPercent: progress,
                                });
                            },
                        });
                    }

                    if (!options.uploadConfig) {
                        updateAttributes({
                            src: blobUrl,
                        });
                    }

                    if (attributes.uploadMetadata) {
                        setImageState({
                            stateType: "loaded",
                            imageLocalUrl: blobUrl,
                        });
                    } else {
                        setImageState({
                            stateType: "preupload",
                            imageLocalUrl: blobUrl,
                            uploadProgressPercent: 0,
                        });
                    }
                } catch (error) {
                    setImageState({
                        stateType: "fetchError",
                        error: error instanceof Error ? error : new Error("Unknown error"),
                        imageLocalUrl: null,
                        isExternalFetchError: !options.uploadConfig || !attributes.uploadMetadata,
                    });
                }
            })();
        }
    }, [imageState, attributes, updateAttributes, options.uploadConfig, editor.isEditable]);

    // Trigger upload
    useEffect(() => {
        if (imageState.stateType === "preupload") {
            setImageState({
                stateType: "upload",
                imageLocalUrl: imageState.imageLocalUrl,
                uploadProgressPercent: 0,
            });
            (async () => {
                if (!options.uploadConfig) {
                    throw new Error("Expected uploadConfig to be defined");
                }

                try {
                    const blob = await getBlobFromUrl(imageState.imageLocalUrl);
                    const uploadData = await options.uploadConfig?.onUploadStaticRef(
                        blob,
                        (progressPercent) => {
                            setImageState({
                                stateType: "upload",
                                imageLocalUrl: imageState.imageLocalUrl,
                                uploadProgressPercent: progressPercent,
                            });
                        },
                    );

                    updateAttributes({
                        src: uploadData.downloadUrl,
                        uploadMetadata: uploadData.uploadMetadata,
                    });

                    setImageState({
                        stateType: "loaded",
                        // Due to eventual consistency, still use the locally downloaded blob URL.
                        // Otherwise, image may not be ready yet to download
                        imageLocalUrl: imageState.imageLocalUrl,
                    });
                } catch (error) {
                    reportError(error);
                    setImageState({
                        stateType: "uploadError",
                        error: error instanceof Error ? error : new Error("Unknown error"),
                        imageLocalUrl: imageState.imageLocalUrl,
                    });
                }
            })();
        }
    }, [imageState, attributes, updateAttributes, options.uploadConfig]);

    const isSelected =
        isEditorEditable && (isSolelySelected || isWithinTextSelection) && isEditorFocused;
    return (
        <NodeViewWrapper
            style={
                attributes.textAlign
                    ? {
                          textAlign: attributes.textAlign,
                      }
                    : undefined
            }
        >
            <div
                data-drag-handle
                className={cn(options?.HTMLAttributes?.class, {
                    selected: isSelected,
                    error: isErrorState(imageState),
                })}
                data-testid="imageNodeView"
                ref={floatingManager.refs.setReference}
            >
                <ImageDisplay
                    imageState={imageState}
                    isEditable={editor.isEditable}
                    isResizable={editor.isEditable && isSelected}
                    expectedWidth={expectedWidth}
                    expectedHeight={expectedHeight}
                    preferredWidth={preferredWidth}
                    preferredHeight={preferredHeight}
                    attributes={attributes}
                    onErrorLoadingLocalUrl={() => {
                        setImageState({
                            stateType: "fetchError",
                            error: new Error("Failed to load cached image"),
                            imageLocalUrl: null,
                            isExternalFetchError: false,
                        });
                    }}
                    onPreferredSizeUpdated={(preferredWidth, preferredHeight) => {
                        updateAttributes({
                            preferredWidth: preferredWidth.toString(),
                            preferredHeight: preferredHeight.toString(),
                        });
                    }}
                    onResizeStop={(width, height) => {
                        updateAttributes({ width, height });
                    }}
                    onDomLoaded={() => {
                        storage.imageStates.set(viewKey, "loadedInDom");
                        onImageStateChange?.(storage.imageStates);
                    }}
                    onLightboxOpen={() => setIsLightboxOpen(true)}
                />
                <ImageMask
                    imageState={imageState}
                    onReloadRequested={() => {
                        setImageState({
                            stateType: "prefetch",
                            imageLocalUrl: null,
                        });
                    }}
                />
                <BTLightbox
                    open={imageState.stateType === "loaded" && isLightboxOpen}
                    onClose={() => setIsLightboxOpen(false)}
                    slides={[
                        {
                            type: "image",
                            src:
                                imageState.stateType === "loaded"
                                    ? imageState.imageLocalUrl
                                    : attributes.src,
                            title: attributes.title ?? "",
                        },
                    ]}
                />
                {isSelected && (
                    <ImageOptionsOverlay
                        editor={editor}
                        node={node}
                        floatingManager={floatingManager}
                    />
                )}
            </div>
        </NodeViewWrapper>
    );
};

interface IImageDisplayProps {
    imageState: ImageState;
    isEditable: boolean;
    /**
     * True when drag-resize handles should be visible. The caller decides this — typically when
     * the editor is editable AND the image is currently selected.
     */
    isResizable: boolean;
    /** User-resized dimensions (or `preferredWidth` fallback) used for rendering. */
    expectedWidth: number | undefined;
    expectedHeight: number | undefined;
    /** Auto-populated natural dimensions used for placeholder size while the image loads. */
    preferredWidth: number | undefined;
    preferredHeight: number | undefined;
    attributes: IImageAttributes;
    onErrorLoadingLocalUrl: () => void;
    onPreferredSizeUpdated: (preferredWidth: number, preferredHeight: number) => void;
    /** Called when the user finishes a drag-resize. Receives the new on-screen size in pixels. */
    onResizeStop: (width: number, height: number) => void;
    /** Called when the underlying `<img>` element fires its `load` event. */
    onDomLoaded: () => void;
    onLightboxOpen: () => void;
}

const minResizeWidth = 50;
const minResizeHeight = 50;

const ImageDisplay = ({
    imageState,
    isEditable,
    isResizable,
    expectedWidth,
    expectedHeight,
    preferredWidth,
    preferredHeight,
    attributes,
    onErrorLoadingLocalUrl,
    onLightboxOpen,
    onPreferredSizeUpdated,
    onResizeStop,
    onDomLoaded,
}: IImageDisplayProps) => {
    const shouldOpenLightboxOnClick = !isEditable && imageState.stateType === "loaded";
    // Editor may be used in webviews, and some use older browsers that might not support aspect-ratio
    const isAspectRatioSupported = useMemo(() => {
        return typeof CSS.supports === "function" && CSS.supports("aspect-ratio: 1 / 1");
    }, []);

    if (imageState.imageLocalUrl) {
        // Always wrap in <Resizable>; show/hide handles via the `enable` prop rather than
        // swapping wrappers, so the image stays mounted across selection changes.
        return (
            <Resizable
                className="imageResizableWrapper"
                size={
                    expectedWidth && expectedHeight
                        ? { width: expectedWidth, height: expectedHeight }
                        : undefined
                }
                minWidth={minResizeWidth}
                minHeight={minResizeHeight}
                lockAspectRatio={
                    expectedWidth && expectedHeight ? expectedWidth / expectedHeight : false
                }
                enable={
                    isResizable
                        ? {
                              top: false,
                              right: true,
                              bottom: true,
                              left: false,
                              topRight: false,
                              bottomRight: true,
                              bottomLeft: false,
                              topLeft: false,
                          }
                        : false
                }
                handleClasses={{
                    right: "imageResizeHandle imageResizeHandleRight",
                    bottom: "imageResizeHandle imageResizeHandleBottom",
                    bottomRight: "imageResizeHandle imageResizeHandleBottomRight",
                }}
                onResizeStart={(e) => {
                    // Prevent ProseMirror's dragstart listener from consuming the mouseup
                    // and leaving re-resizable's resize stuck. mousemove/mouseup still fire.
                    e.preventDefault();
                }}
                onResizeStop={(_e, _dir, ref) => {
                    onResizeStop(ref.offsetWidth, ref.offsetHeight);
                }}
            >
                <img
                    data-testid="editorImage"
                    className="imageDisplay"
                    src={imageState.imageLocalUrl}
                    alt={attributes.alt}
                    // eslint-disable-next-line react/forbid-dom-props
                    title={attributes.title}
                    onError={onErrorLoadingLocalUrl}
                    onClick={shouldOpenLightboxOnClick ? onLightboxOpen : undefined}
                    style={
                        // Fill the wrapper when its size is known so resize handles stay aligned.
                        // Without a known size the wrapper is auto-sized; filling it would collapse to 0.
                        expectedWidth && expectedHeight
                            ? { width: "100%", height: "100%", display: "block" }
                            : undefined
                    }
                    // After loading, record the image height/width if in edit mode
                    onLoad={(e) => {
                        onDomLoaded();
                        if (!isEditable) {
                            return;
                        }

                        const img = e.target as HTMLImageElement;
                        onPreferredSizeUpdated(img.naturalWidth, img.naturalHeight);
                    }}
                />
            </Resizable>
        );
    }

    const width = expectedWidth ?? preferredWidth ?? 256;
    const height = expectedHeight ?? preferredHeight ?? 144;

    return (
        <BTRow
            className="imageDisplay imagePlaceholder"
            responsiveMode="viewport"
            justify="center"
            align="middle"
            style={
                isAspectRatioSupported
                    ? {
                          width,
                          height: "auto",
                          aspectRatio: `${width}/${height}`,
                      }
                    : {
                          width,
                          height,
                      }
            }
        />
    );
};

interface IImageMaskProps {
    imageState: ImageState;
    onReloadRequested: () => void;
}

const ImageMask = ({ imageState, onReloadRequested }: IImageMaskProps) => {
    const progressPercent = useMemo<number | null>(() => {
        switch (imageState.stateType) {
            case "prefetch":
            case "fetching":
            case "fetchError":
            case "loaded":
                return null;
            case "uploadError":
                return 100;
            case "preupload":
                return 0;
            case "upload":
                return imageState.uploadProgressPercent;
        }
    }, [imageState]);

    const maskCenterIcon = useMemo(() => {
        switch (imageState.stateType) {
            case "prefetch":
            case "fetching":
                return <BTIconLoadingOutlined size="extraLarge" />;
            case "fetchError":
                return imageState.isExternalFetchError ? (
                    <>
                        <BTIconImageOutlined size={64} />
                        <span>Image source not supported</span>
                    </>
                ) : (
                    <BdsButton
                        displayType="secondary"
                        testid="refetchImage"
                        ariaLabel="Reload image"
                        icon={<BdsIconArrowClockwise size="lg" />}
                        onClick={onReloadRequested}
                        tooltip="Reload image"
                    />
                );
            case "preupload":
            case "upload":
            case "uploadError":
            case "loaded":
                return <BTIconImageOutlined size={64} />;
        }
    }, [imageState, onReloadRequested]);

    if (imageState.stateType === "loaded") {
        return null;
    }

    return (
        <div
            className={cn("imageMask", {
                blurred: imageState.imageLocalUrl,
            })}
        >
            <BTRow
                className="imageMaskIconContainer"
                responsiveMode="viewport"
                justify="center"
                align="middle"
                style={{
                    flexDirection: "column",
                    color: isErrorState(imageState) ? ErrorColor : PrimaryColor,
                }}
            >
                {maskCenterIcon}
            </BTRow>
            {progressPercent !== null && (
                <div
                    className="padding-horizontal-sm"
                    style={{ position: "absolute", width: "100%", bottom: 0 }}
                >
                    <Progress
                        percent={progressPercent}
                        showInfo={false}
                        strokeColor={isErrorState(imageState) ? ErrorColor : PrimaryColor}
                    />
                </div>
            )}
        </div>
    );
};
